import React from 'react';
import PorovnavacBankaKarta from './PorovnavacBankaKarta';
import { apiUrl } from './config/apiBase';

const eur = (x) => {
  const v = Number(x ?? 0);
  const safe = Math.abs(v) < 0.5 ? 0 : v;
  return safe.toLocaleString('sk-SK');
};

const ziskajUpozorneniaText = (banka) => {
  if (Array.isArray(banka?.upozorneniaText) && banka.upozorneniaText.length) {
    return banka.upozorneniaText;
  }
  if (Array.isArray(banka?.upozornenia) && banka.upozornenia.length) {
    return banka.upozornenia.map((u) => u?.text).filter(Boolean);
  }
  return [];
};

const ziskajBankyZResponse = (data) => {
  if (Array.isArray(data)) return data;
  if (Array.isArray(data?.banky)) return data.banky;
  return [];
};

const jeNedostupnaBanka = (b) => (
  b?.nedostupnost?.nemaFixaciu === true ||
  b?.nedostupnost?.nemaTypHypoteky === true ||
  b?.nedostupnost?.nemaTypHypotekyPreBanku === true ||
  b?.nedostupnost?.nemaFixaciuPreBanku === true
);

const zoradBanky = (a, b) => {
  const aNed = jeNedostupnaBanka(a);
  const bNed = jeNedostupnaBanka(b);

  // 1) nedostupné vždy na koniec
  if (aNed !== bNed) return aNed ? 1 : -1;

  // 2) potom až triediť podľa sadzby
  const aRate = Number.isFinite(Number(a?.finalRate)) ? Number(a.finalRate) : Infinity;
  const bRate = Number.isFinite(Number(b?.finalRate)) ? Number(b.finalRate) : Infinity;

  return aRate - bRate;
};

function PorovnavacSlider({ banky, fixacia, suma, ltvPercenta, splatnostRoky, eur, ziskajUpozorneniaText }) {
  const sliderRef = React.useRef(null);
  const wrapperRef = React.useRef(null);
  const [zobrazSipky, setZobrazSipky] = React.useState(false);

  const skontrolujSipky = React.useCallback(() => {
    const el = sliderRef.current;
    if (!el) return;
    setZobrazSipky(el.scrollWidth > el.clientWidth + 2);
  }, []);

  React.useEffect(() => {
    skontrolujSipky();

    const el = sliderRef.current;
    if (!el) return;

    const onScroll = () => skontrolujSipky();
    el.addEventListener('scroll', onScroll, { passive: true });

    const ro = new ResizeObserver(() => skontrolujSipky());
    ro.observe(el);
    if (wrapperRef.current) ro.observe(wrapperRef.current);

    window.addEventListener('resize', skontrolujSipky);

    return () => {
      el.removeEventListener('scroll', onScroll);
      ro.disconnect();
      window.removeEventListener('resize', skontrolujSipky);
    };
  }, [banky?.length, skontrolujSipky]);

  const scrollLeft = () => {
    if (!sliderRef.current) return;
    sliderRef.current.scrollBy({ left: -320, behavior: 'smooth' });
  };

  const scrollRight = () => {
    if (!sliderRef.current) return;
    sliderRef.current.scrollBy({ left: 320, behavior: 'smooth' });
  };

  return (
    <div className="porovnavac-slider-wrapper" ref={wrapperRef}>
      {zobrazSipky && (
        <button type="button" className="slider-arrow left" onClick={scrollLeft} aria-label="Doľava">
          ‹
        </button>
      )}

      <div className="porovnavac-slider" ref={sliderRef}>
        {banky.map((b) => (
          <PorovnavacBankaKarta
            key={`${b?.bankaKod ?? 'BANKA'}-${b?.jeAtipickaFixacia ? `M${b?.fixaciaMesiace}` : `R${fixacia}`}`}
            banka={b}
            suma={suma}
            ltvPercenta={ltvPercenta}
            eur={eur}
            upozorneniaText={ziskajUpozorneniaText(b)}
            fixaciaRoky={fixacia}
            splatnostRoky={splatnostRoky}
          />
        ))}
      </div>

      {zobrazSipky && (
        <button type="button" className="slider-arrow right" onClick={scrollRight} aria-label="Doprava">
            ›
        </button>
        )}
    </div>
  );
}

function PorovnavacBank(props) {
  const {
    typHypoteky,
    suma,
    roky,
    hodnotazabezpecenia,
    fixacia,

    pocetziadatelov,
    pocetdeti,
    pocetdeti2,
    pocetdeti3,
    pocetdeti4,

    spolocnadomacnost1,
    spolocnadomacnost2,

    vek1, prijem1,
    vek2, prijem2,
    vek3, prijem3,
    vek4, prijem4,

    Hypoobjem,
    Hyposplatnost,
    Hyposadzba,
    Hyposplatka,
    Hypofixacia,
    Ostatneobjem,
    Ostatnesplatka,

    Hypoobjem2,
    Hyposplatnost2,
    Hyposadzba2,
    Hyposplatka2,
    Hypofixacia2,
    Ostatneobjem2,
    Ostatnesplatka2,

    Hypoobjem3,
    Hyposplatnost3,
    Hyposadzba3,
    Hyposplatka3,
    Hypofixacia3,
    Ostatneobjem3,
    Ostatnesplatka3,

    Hypoobjem4,
    Hyposplatnost4,
    Hyposadzba4,
    Hyposplatka4,
    Hypofixacia4,
    Ostatneobjem4,
    Ostatnesplatka4,

    onZmenaDatPrePdf,
  } = props;

  // 4 checkboxy (default ON) – názvy presne podľa BE (Postman/processor)
  const [maUcet, setMaUcet] = React.useState(true);
  const [maPoistenieUveru, setMaPoistenieUveru] = React.useState(true);
  const [maPoistenieNehnutelnosti, setMaPoistenieNehnutelnosti] = React.useState(true);
  const [maEkoCertifikat, setMaEkoCertifikat] = React.useState(true);

  // banky + výber bánk (po prvom prepočte)
  const [zoznamBank, setZoznamBank] = React.useState([]); // [{bankaKod,bankaNazov}]
  const [vsetkyBanky, setVsetkyBanky] = React.useState(true);
  const [vybraneBanky, setVybraneBanky] = React.useState({}); // bankaKod -> boolean

  const [banky, setBanky] = React.useState([]);
  const [jeLoading, setJeLoading] = React.useState(false);
  const [chyba, setChyba] = React.useState(null);

  const nacitajZoznamBank = React.useCallback(async () => {
    try {
      const body = {
        typHypoteky: typHypoteky,
        suma: Number(suma) || 0,
        roky: Number(roky) || 0,
        hodnotaNehnutelnosti: Number(hodnotazabezpecenia) || 0,
        fixaciaRoky: Number(fixacia) || null,
        fixaciaMesiace: null,

        pocetZiadatelov: Number(pocetziadatelov) || 0,

        pocetDeti: Number(pocetdeti) || 0,
        pocetDeti2: Number(pocetdeti2) || 0,
        pocetDeti3: Number(pocetdeti3) || 0,
        pocetDeti4: Number(pocetdeti4) || 0,

        spolocnadomacnost1: !!spolocnadomacnost1,
        spolocnadomacnost2: !!spolocnadomacnost2,

        vek1: Number(vek1) || 0,
        prijem1: Number(prijem1) || 0,
        vek2: Number(vek2) || 0,
        prijem2: Number(prijem2) || 0,
        vek3: Number(vek3) || 0,
        prijem3: Number(prijem3) || 0,
        vek4: Number(vek4) || 0,
        prijem4: Number(prijem4) || 0,

        prijemPartner: 0,

        zavazky: {
          hypoteky: {
            hypoobjem: Number(Hypoobjem) || 0,
            hyposplatnost: Number(Hyposplatnost) || 0,
            hyposadzba: Number(Hyposadzba) || 0,
            hyposplatka: Number(Hyposplatka) || 0,
            hypofixacia: Number(Hypofixacia) || 0,

            hypoobjem2: Number(Hypoobjem2) || 0,
            hyposplatnost2: Number(Hyposplatnost2) || 0,
            hyposadzba2: Number(Hyposadzba2) || 0,
            hyposplatka2: Number(Hyposplatka2) || 0,
            hypofixacia2: Number(Hypofixacia2) || 0,

            hypoobjem3: Number(Hypoobjem3) || 0,
            hyposplatnost3: Number(Hyposplatnost3) || 0,
            hyposadzba3: Number(Hyposadzba3) || 0,
            hyposplatka3: Number(Hyposplatka3) || 0,
            hypofixacia3: Number(Hypofixacia3) || 0,

            hypoobjem4: Number(Hypoobjem4) || 0,
            hyposplatnost4: Number(Hyposplatnost4) || 0,
            hyposadzba4: Number(Hyposadzba4) || 0,
            hyposplatka4: Number(Hyposplatka4) || 0,
            hypofixacia4: Number(Hypofixacia4) || 0,
          },
          spotrebaky: {
            spotrobjem: Number(Ostatneobjem) || 0,
            spotsplatnost: 0,
            spotsplatka: Number(Ostatnesplatka) || 0,

            spotrobjem2: Number(Ostatneobjem2) || 0,
            spotsplatnost2: 0,
            spotsplatka2: Number(Ostatnesplatka2) || 0,

            spotrobjem3: Number(Ostatneobjem3) || 0,
            spotsplatnost3: 0,
            spotsplatka3: Number(Ostatnesplatka3) || 0,

            spotrobjem4: Number(Ostatneobjem4) || 0,
            spotsplatnost4: 0,
            spotsplatka4: Number(Ostatnesplatka4) || 0,
          },
        },

        ineZavazky: 0,
        inaSplatka: 0,
        danovyBonusDeti: 0,

        maUcet: !!maUcet,
        maPoistenieUveru: !!maPoistenieUveru,
        maPoistenieNehnutelnosti: !!maPoistenieNehnutelnosti,
        maEkoCertifikat: !!maEkoCertifikat,
      };

      const res = await fetch(apiUrl('/api/porovnavac'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(body),
      });

      if (!res.ok) return;

      const data = await res.json();
      const arr = ziskajBankyZResponse(data);

      const unik = new Map();
      arr.forEach((b) => {
        const kod = b?.bankaKod;
        if (!kod) return;
        if (!unik.has(kod)) {
          unik.set(kod, { bankaKod: kod, bankaNazov: b?.bankaNazov ?? kod });
        }
      });

      const list = Array.from(unik.values()).sort((a, b) =>
        String(a.bankaNazov).localeCompare(String(b.bankaNazov), 'sk')
      );

      setZoznamBank(list);

      // vždy keď získame list, nastav default: všetky zapnuté (ak ešte nie sú)
      setVybraneBanky((prev) => {
        if (prev && Object.keys(prev).length) return prev;
        const init = {};
        list.forEach((x) => { init[x.bankaKod] = true; });
        return init;
      });

      setVsetkyBanky(true);
    } catch (e) {
      // ticho – len zoznam bánk; nech to nepíše chybu do UI
    }
  }, [
    typHypoteky, suma, roky, hodnotazabezpecenia, fixacia,
    pocetziadatelov, pocetdeti, pocetdeti2, pocetdeti3, pocetdeti4,
    vek1, prijem1, vek2, prijem2, vek3, prijem3, vek4, prijem4, spolocnadomacnost1, spolocnadomacnost2,
    Hypoobjem, Hyposplatnost, Hyposplatka, Ostatneobjem, Ostatnesplatka,
    maUcet, maPoistenieUveru, maPoistenieNehnutelnosti, maEkoCertifikat,
    Hyposadzba, Hypofixacia,
    Hypoobjem2, Hyposplatnost2, Hyposadzba2, Hyposplatka2, Hypofixacia2, Ostatneobjem2, Ostatnesplatka2,
    Hypoobjem3, Hyposplatnost3, Hyposadzba3, Hyposplatka3, Hypofixacia3, Ostatneobjem3, Ostatnesplatka3,
    Hypoobjem4, Hyposplatnost4, Hyposadzba4, Hyposplatka4, Hypofixacia4, Ostatneobjem4, Ostatnesplatka4,
  ]);

  React.useEffect(() => {
    if (zoznamBank.length) return;
    nacitajZoznamBank();
  }, [zoznamBank.length, nacitajZoznamBank]);

  const vypocitajPorovnanie = React.useCallback(async () => {
    setJeLoading(true);
    setChyba(null);

    try {
      const body = {
        typHypoteky: typHypoteky,
        suma: Number(suma) || 0,
        roky: Number(roky) || 0,
        hodnotaNehnutelnosti: Number(hodnotazabezpecenia) || 0,
        fixaciaRoky: Number(fixacia) || null,
        fixaciaMesiace: null,

        pocetZiadatelov: Number(pocetziadatelov) || 0,

        pocetDeti: Number(pocetdeti) || 0,
        pocetDeti2: Number(pocetdeti2) || 0,
        pocetDeti3: Number(pocetdeti3) || 0,
        pocetDeti4: Number(pocetdeti4) || 0,

        spolocnadomacnost1: !!spolocnadomacnost1,
        spolocnadomacnost2: !!spolocnadomacnost2,

        vek1: Number(vek1) || 0,
        prijem1: Number(prijem1) || 0,
        vek2: Number(vek2) || 0,
        prijem2: Number(prijem2) || 0,
        vek3: Number(vek3) || 0,
        prijem3: Number(prijem3) || 0,
        vek4: Number(vek4) || 0,
        prijem4: Number(prijem4) || 0,

        prijemPartner: 0,

        zavazky: {
          hypoteky: {
            hypoobjem: Number(Hypoobjem) || 0,
            hyposplatnost: Number(Hyposplatnost) || 0,
            hyposadzba: Number(Hyposadzba) || 0,
            hyposplatka: Number(Hyposplatka) || 0,
            hypofixacia: Number(Hypofixacia) || 0,

            hypoobjem2: Number(Hypoobjem2) || 0,
            hyposplatnost2: Number(Hyposplatnost2) || 0,
            hyposadzba2: Number(Hyposadzba2) || 0,
            hyposplatka2: Number(Hyposplatka2) || 0,
            hypofixacia2: Number(Hypofixacia2) || 0,

            hypoobjem3: Number(Hypoobjem3) || 0,
            hyposplatnost3: Number(Hyposplatnost3) || 0,
            hyposadzba3: Number(Hyposadzba3) || 0,
            hyposplatka3: Number(Hyposplatka3) || 0,
            hypofixacia3: Number(Hypofixacia3) || 0,

            hypoobjem4: Number(Hypoobjem4) || 0,
            hyposplatnost4: Number(Hyposplatnost4) || 0,
            hyposadzba4: Number(Hyposadzba4) || 0,
            hyposplatka4: Number(Hyposplatka4) || 0,
            hypofixacia4: Number(Hypofixacia4) || 0,
          },
          spotrebaky: {
            spotrobjem: Number(Ostatneobjem) || 0,
            spotsplatnost: 0,
            spotsplatka: Number(Ostatnesplatka) || 0,

            spotrobjem2: Number(Ostatneobjem2) || 0,
            spotsplatnost2: 0,
            spotsplatka2: Number(Ostatnesplatka2) || 0,

            spotrobjem3: Number(Ostatneobjem3) || 0,
            spotsplatnost3: 0,
            spotsplatka3: Number(Ostatnesplatka3) || 0,

            spotrobjem4: Number(Ostatneobjem4) || 0,
            spotsplatnost4: 0,
            spotsplatka4: Number(Ostatnesplatka4) || 0,
          },
        },

        ineZavazky: 0,
        inaSplatka: 0,
        danovyBonusDeti: 0,

        maUcet: !!maUcet,
        maPoistenieUveru: !!maPoistenieUveru,
        maPoistenieNehnutelnosti: !!maPoistenieNehnutelnosti,
        maEkoCertifikat: !!maEkoCertifikat,
      };

      const res = await fetch(apiUrl('/api/porovnavac'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(body),
      });

      if (!res.ok) {
        throw new Error(`API /api/porovnavac vrátilo chybu: ${res.status}`);
      }

      const data = await res.json();
      const arr = ziskajBankyZResponse(data);

      // zoznam bánk na checkboxy (DB-driven cez BE response)
      const unik = new Map();
      arr.forEach((b) => {
        const kod = b?.bankaKod;
        if (!kod) return;
        if (!unik.has(kod)) {
          unik.set(kod, { bankaKod: kod, bankaNazov: b?.bankaNazov ?? kod });
        }
      });
      const list = Array.from(unik.values()).sort((a, b) =>
        String(a.bankaNazov).localeCompare(String(b.bankaNazov), 'sk')
      );

      setZoznamBank(list);

      // init výberu bánk (1. prepočet)
      setVybraneBanky((prev) => {
        if (Object.keys(prev || {}).length) return prev;
        const init = {};
        list.forEach((x) => { init[x.bankaKod] = true; });
        return init;
      });

      setVsetkyBanky(true);

      // zoradenie: najnižší finalRate hore, “nedostupné” (bez čísla) na koniec
      const zoradene = [...arr].sort(zoradBanky);
      setBanky(zoradene);
    } catch (e) {
      setChyba(e?.message || 'Neznáma chyba pri prepočte.');
      setBanky([]);
      setZoznamBank([]);
      setVybraneBanky({});
      setVsetkyBanky(true);
    } finally {
      setJeLoading(false);
    }
  }, [
    typHypoteky, suma, roky, hodnotazabezpecenia, fixacia,
    pocetziadatelov, pocetdeti, pocetdeti2, pocetdeti3, pocetdeti4,
    vek1, prijem1, vek2, prijem2, vek3, prijem3, vek4, prijem4, spolocnadomacnost1, spolocnadomacnost2,
    Hypoobjem, Hyposplatnost, Hyposplatka, Ostatneobjem, Ostatnesplatka,
    maUcet, maPoistenieUveru, maPoistenieNehnutelnosti, maEkoCertifikat,
    Hyposadzba, Hypofixacia,
    Hypoobjem2, Hyposplatnost2, Hyposadzba2, Hyposplatka2, Hypofixacia2, Ostatneobjem2, Ostatnesplatka2,
    Hypoobjem3, Hyposplatnost3, Hyposadzba3, Hyposplatka3, Hypofixacia3, Ostatneobjem3, Ostatnesplatka3,
    Hypoobjem4, Hyposplatnost4, Hyposadzba4, Hyposplatka4, Hypofixacia4, Ostatneobjem4, Ostatnesplatka4,
  ]);

  const ltvPercenta = React.useMemo(() => {
    const s = Number(suma) || 0;
    const h = Number(hodnotazabezpecenia) || 0;
    if (h <= 0) return null;
    return (s / h) * 100;
  }, [suma, hodnotazabezpecenia]);

  const zobrazeneBanky = React.useMemo(() => {
    if (!banky.length) return [];
    const filt = banky.filter((b) => {
      const kod = b?.bankaKod;
      if (!kod) return true;
      const on = vybraneBanky?.[kod];
      return on !== false;
    });

    // pre istotu: nech “nedostupné” (bez finalRate) idú až na koniec aj po filtrovaní
    return [...filt].sort(zoradBanky);
  }, [banky, vybraneBanky]);

  React.useEffect(() => {
    if (typeof onZmenaDatPrePdf !== 'function') return;

    onZmenaDatPrePdf({
      banky,
      zobrazeneBanky,
      zoznamBank,
      vybraneBanky,
      vsetkyBanky,
      maUcet,
      maPoistenieUveru,
      maPoistenieNehnutelnosti,
      maEkoCertifikat,
      fixacia,
      suma,
      roky,
      ltvPercenta,
      jeLoading,
      chyba,
    });
  }, [
    onZmenaDatPrePdf,
    banky,
    zobrazeneBanky,
    zoznamBank,
    vybraneBanky,
    vsetkyBanky,
    maUcet,
    maPoistenieUveru,
    maPoistenieNehnutelnosti,
    maEkoCertifikat,
    fixacia,
    suma,
    roky,
    ltvPercenta,
    jeLoading,
    chyba,
  ]);

  const prepniVsetky = (novyStav) => {
    setVsetkyBanky(novyStav);
    setVybraneBanky((prev) => {
      const next = { ...(prev || {}) };
      zoznamBank.forEach((b) => { next[b.bankaKod] = novyStav; });
      return next;
    });
  };

  const prepniJednu = (kod, novyStav) => {
    setVybraneBanky((prev) => {
      const next = { ...(prev || {}) };
      next[kod] = novyStav;
      return next;
    });

    // sync "Všetky banky"
    if (!novyStav) {
      setVsetkyBanky(false);
      return;
    }

    // ak všetky sú true → vsetkyBanky true
    const allOn = zoznamBank.every((b) => {
      if (b.bankaKod === kod) return novyStav;
      return (vybraneBanky?.[b.bankaKod] !== false);
    });
    setVsetkyBanky(allOn);
  };

  return (
    <div className="card-section card--full">
      <div className="prepay-head">
        <h2>Porovnávač bánk</h2>
      </div>

      {/* BLOK 1 – Výber bánk (najprv banky) */}
      <div className="form-group">
        <div className="toggle-group porovnavac-banky-row">
          <span className="porovnavac-row-label">Porovnať:</span>
            <label className="chk porovnavac-banka-item">
            <input
                type="checkbox"
                checked={vsetkyBanky}
                onChange={(e) => prepniVsetky(e.target.checked)}
                disabled={!zoznamBank.length}
            />
            Všetky banky 
            </label>

            {zoznamBank.map((b) => (
            <label
                key={b.bankaKod}
                className="chk porovnavac-banka-item"
                title={b.bankaNazov}
            >
                <input
                type="checkbox"
                checked={vybraneBanky?.[b.bankaKod] !== false}
                onChange={(e) => prepniJednu(b.bankaKod, e.target.checked)}
                />
                {b.bankaKod}
            </label>
            ))}
        </div>
        </div>

      <div className="porovnavac-divider"></div>
      {/* BLOK 2 – 4 checkboxy (až po bankách) */}
      <div className="form-group porovnavac-xsell-row">
         <span className="porovnavac-row-label">Vybrať:</span>
        <div className="toggle-group porovnavac-xsell">
          <label className="chk porovnavac-xsell-item">
            <input
              type="checkbox"
              checked={maUcet}
              onChange={(e) => setMaUcet(e.target.checked)}
            />
            Účet
          </label>

          <label className="chk porovnavac-xsell-item">
            <input
              type="checkbox"
              checked={maPoistenieUveru}
              onChange={(e) => setMaPoistenieUveru(e.target.checked)}
            />
            Poistenie úveru
          </label>

          <label className="chk porovnavac-xsell-item">
            <input
              type="checkbox"
              checked={maPoistenieNehnutelnosti}
              onChange={(e) => setMaPoistenieNehnutelnosti(e.target.checked)}
            />
            Poistenie nehnuteľnosti
          </label>

          <label className="chk porovnavac-xsell-item">
            <input
              type="checkbox"
              checked={maEkoCertifikat}
              onChange={(e) => setMaEkoCertifikat(e.target.checked)}
            />
            EKO certifikát
          </label>
        </div>

        <button
          type="button"
          onClick={vypocitajPorovnanie}
          disabled={jeLoading}
          className="action-btn"
        >
          {jeLoading ? 'Prepočítavam…' : 'Prepočítať'}
        </button>
      </div>

      {chyba && (
        <div className="porovnavac-chyba">
          {chyba}
        </div>
      )}

      {/* Výsledky – slider + šípky desktop only */}
      {zobrazeneBanky.length > 0 && (
        <PorovnavacSlider
          banky={zobrazeneBanky}
          fixacia={fixacia}
          suma={suma}
          ltvPercenta={ltvPercenta}
          splatnostRoky={roky}
          eur={eur}
          ziskajUpozorneniaText={ziskajUpozorneniaText}
        />
      )}
    </div>
  );
}

export default PorovnavacBank;